import React from 'react';
import {
  AlertCircle,
  Calendar,
  Loader2,
  Package,
  TrendingUp,
} from 'lucide-react';
import { useLotesIngrediente } from '../hooks/useIngredientes';
import type { LoteIngrediente } from '../services/ingredientesService';
import { formatearFechaCorta } from '../utils/dateUtils';

interface Props {
  ingredienteId: number;
}

const fmtCantidad = (n: number, dec = 2) =>
  n.toLocaleString('es-CL', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });

const fmtDinero = (n: number) =>
  n.toLocaleString('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const fmtFecha = (iso: string | null) => {
  if (!iso) return '—';
  return formatearFechaCorta(iso) || '—';
};

const badgeEstado: Record<LoteIngrediente['estado'], string> = {
  disponible:
    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  proximo_vencer:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  vencido: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  agotado: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

const labelEstado: Record<LoteIngrediente['estado'], string> = {
  disponible: 'Disponible',
  proximo_vencer: 'Por vencer',
  vencido: 'Vencido',
  agotado: 'Agotado',
};

const describirFormato = (
  lote: LoteIngrediente,
  unidadPack: string | null
): string => {
  if (lote.es_pack && lote.cantidad_packs && lote.unidades_por_pack && unidadPack) {
    return `${fmtCantidad(lote.cantidad_packs, 0)} × ${fmtCantidad(
      lote.unidades_por_pack,
      0
    )} ${unidadPack}`;
  }
  if (lote.formato_compra) return lote.formato_compra;
  return `${fmtCantidad(lote.cantidad, 2)}${unidadPack ? ` ${unidadPack}` : ''}`;
};

export const IngredienteLotesPanel: React.FC<Props> = ({ ingredienteId }) => {
  const { data, isLoading, error } = useLotesIngrediente(ingredienteId);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Cargando lotes…</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-red-200/60 dark:border-red-800/60 p-4 flex items-center gap-2">
        <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
        <span className="text-sm text-red-700 dark:text-red-300">
          Error al cargar lotes del ingrediente.
        </span>
      </div>
    );
  }

  const { ingrediente, lotes, totales } = data;
  const displayUnit = ingrediente.unidad_display_simbolo ?? '';
  const baseUnit = ingrediente.unidad_base_simbolo ?? '';

  return (
    <div className="space-y-3">
      {/* Totales del ingrediente */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
              Stock total
            </p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {fmtCantidad(ingrediente.stock_actual, 2)} {displayUnit}
            </p>
            {baseUnit && baseUnit !== displayUnit && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {fmtCantidad(ingrediente.stock_actual_base, 0)} {baseUnit}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
              Valorización
            </p>
            <p className="text-xl font-bold text-green-700 dark:text-green-400">
              {fmtDinero(totales.valorizacion_total)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Costo promedio: {fmtDinero(totales.costo_promedio_base)}
              {baseUnit ? ` / ${baseUnit}` : ''}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
              Lotes activos
            </p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {lotes.length}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Ordenados FIFO por vencimiento
            </p>
          </div>
        </div>
      </div>

      {lotes.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-4 text-center text-sm text-gray-500 dark:text-gray-400">
          Este ingrediente todavía no tiene lotes registrados. Los lotes se
          crean automáticamente al cargar una factura de compra.
        </div>
      ) : (
        lotes.map((lote) => {
          const packUnit = lote.unidad_pack_simbolo ?? displayUnit;
          return (
            <div
              key={lote.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-4"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg shrink-0">
                    <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {describirFormato(lote, packUnit)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      Lote {lote.numero_lote}
                      {lote.numero_factura
                        ? ` · Factura ${lote.numero_factura}`
                        : ''}
                    </p>
                  </div>
                </div>
                <span
                  className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full shrink-0 ${badgeEstado[lote.estado]}`}
                >
                  {labelEstado[lote.estado]}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Comprado
                  </p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {fmtFecha(lote.fecha_ingreso)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Vence
                  </p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {fmtFecha(lote.fecha_vencimiento)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Disponible
                  </p>
                  <p
                    className="font-semibold text-gray-900 dark:text-gray-100"
                    title={`${fmtCantidad(lote.cantidad_disponible_base, 0)} ${baseUnit}`}
                  >
                    {fmtCantidad(lote.cantidad_disponible, 2)} {displayUnit}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" /> Valor
                  </p>
                  <p className="font-semibold text-green-700 dark:text-green-400">
                    {fmtDinero(lote.valorizacion_lote)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {fmtDinero(lote.costo_unitario_base)}
                    {baseUnit ? ` / ${baseUnit}` : ''}
                  </p>
                </div>
              </div>
              {lote.proveedor_nombre && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  Proveedor: {lote.proveedor_nombre}
                </p>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default IngredienteLotesPanel;
